// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// ConvexClientProvider — wraps the tree in Convex's <ConvexProvider> ONLY when
// the experiment is active (#191). Otherwise it is a transparent passthrough,
// so the production path renders an identical tree with no Convex client and no
// websocket. MSAL stays the app's auth; Convex here is a pure data layer.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { useEffect } from "react";
import { ConvexProviderWithAuth } from "convex/react";
import { convexClient } from "./convexClient";
import { CONVEX_CONFIGURED } from "../utility/backendConfig";
import { useMsalConvexAuth } from "./useMsalConvexAuth";
import { setProdConvexTokenProvider } from "./convexRead";

// A→B (#388): Time Machine reads the PROD box (bright-bear) through a SEPARATE
// ConvexHttpClient in convexRead.js. That client gets NO ambient auth from
// ConvexProviderWithAuth, but every TM function is requireUser-gated (#231) — so
// without this bridge the whole of Time Machine, the history sections and Ticket
// Master fail with "Unauthenticated" for convexOnly users. Handing the same MSAL
// ID token to the plain module fixes it. Rendered INSIDE the provider so the hook
// has MSAL context; renders nothing.
function ProdBoxAuthBridge() {
  const { fetchAccessToken } = useMsalConvexAuth();
  useEffect(() => {
    setProdConvexTokenProvider(fetchAccessToken);
    return () => setProdConvexTokenProvider(null);
  }, [fetchAccessToken]);
  return null;
}

export default function ConvexClientProvider({ children }) {
  // Mount the provider whenever Convex is configured. The websocket only
  // actually opens when a component subscribes (ConvexBridge/Badge do so only
  // for convexOnly/both users), so mongoOnly users incur no connection.
  //
  // #201 login-lock: ConvexProviderWithAuth forwards the Keller Microsoft
  // (Azure AD) ID token (via useMsalConvexAuth) to Convex, so requireUser() on
  // the server can gate every data function. MSAL stays the app's auth; this
  // just hands the existing identity to Convex.
  if (CONVEX_CONFIGURED && convexClient) {
    return (
      <ConvexProviderWithAuth client={convexClient} useAuth={useMsalConvexAuth}>
        <ProdBoxAuthBridge />
        {children}
      </ConvexProviderWithAuth>
    );
  }
  return children;
}
